import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OrgService } from './org.service';
import { Observable, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

export interface OrgInitResult {
  success: boolean;
  orgSlug: string | null;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class OrgInitService {
  constructor(
    private orgService: OrgService,
    private router: Router
  ) { }

  /**
   * Initialize organization validation flow
   * Returns the organization slug if valid, null otherwise
   */
  initializeOrgValidation(): Observable<OrgInitResult> {
    console.log('[OrgInitService] Starting org validation...');
    
    // Step 1: Check for subdomain
    const subdomain = this.orgService.getSubdomainFromUrl();
    const domain = this.orgService.getDomainFromUrl();
    let domainToValidate = subdomain ? subdomain : domain;
    console.log('[OrgInitService] Subdomain detected:', subdomain);

    if (domainToValidate) {
      // Subdomain exists - validate it
      console.log('[OrgInitService] Validating domain/subdomain:', domainToValidate);
      return this.orgService.validateOrgSlug(domainToValidate).pipe(
        switchMap(response => {
          console.log('[OrgInitService] Subdomain validation response:', response);
          if (response) {
            // Valid subdomain - store and continue
            this.orgService.storeOrgSlug(response.OrgName);
            this.orgService.setCurrentOrganization(response);
            return of({
              success: true,
              orgSlug: response.OrgCode
            });
          } else {
            // Invalid subdomain - show org input screen
            return of({
              success: false,
              orgSlug: null,
              errorMessage: 'Invalid organization'
            });
          }
        }),
        catchError(error => {
          // Error validating subdomain - show org input screen
          console.error('[OrgInitService] Error validating subdomain:', error);
          return of({
            success: false,
            orgSlug: null,
            errorMessage: 'Error validating organization'
          });
        })
      );
    }

    // Step 2: No subdomain - check localStorage
    const storedOrgSlug = this.orgService.getStoredOrgSlug();
    console.log('[OrgInitService] Stored org slug:', storedOrgSlug);

    if (storedOrgSlug) {
      // Stored org exists - validate it
      console.log('[OrgInitService] Validating stored org:', storedOrgSlug);
      return this.orgService.validateOrgSlug(storedOrgSlug).pipe(
        switchMap(response => {
          console.log('[OrgInitService] Stored org validation response:', response);
          if (response) {
            this.orgService.setCurrentOrganization(response);
            return of({
              success: true,
              orgSlug: response.OrgName
            });
          } else {
            // Invalid stored org - clear storage and show input screen
            this.orgService.clearOrgSlug();
            this.orgService.clearCurrentOrganization();
            return of({
              success: false,
              orgSlug: null,
              errorMessage: 'Stored organization is no longer valid'
            });
          }
        }),
        catchError(error => {
          // Error validating stored org - clear and show input screen
          console.error('[OrgInitService] Error validating stored org:', error);
          this.orgService.clearOrgSlug();
          this.orgService.clearCurrentOrganization();
          return of({
            success: false,
            orgSlug: null,
            errorMessage: 'Error validating stored organization'
          });
        })
      );
    }

    // Step 3: No subdomain and no stored org - show org input screen
    console.log('[OrgInitService] No org found, showing input screen');
    return of({
      success: false,
      orgSlug: null,
      errorMessage: 'No organization selected'
    });
  }
}
